
<script>
import { layoutMethods, eloadDashboardMethods, eloadDashboardComputed } from "@/state/helpers";
const { Tooltip } = require('bootstrap');
import moment from 'moment';
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

export default {
  page: {
    title: "E-Load Dashboard",
  },
  components: {
    flatPickr
  },
  data() {
    return {
      tooltips: {},
      activeTab: 'business-segment',
      summaryTab: 'business-segment',
      dailyTransactionTab: 'peddlr-lite',
      comingSoonOverlay: false, // Set to false to disable overlay
      dateRange: {
        start: moment().subtract(7, 'days').format('YYYY-MM-DD'),
        end: moment().subtract(1, 'days').format('YYYY-MM-DD')
      },
      dateRangeString: '',
      flatpickrInstance: null,
      flatpickrConfig: {
        mode: 'range',
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'j M Y',
        // limit selection up to yesterday
        maxDate: moment().subtract(1, 'days').format('YYYY-MM-DD'),
        // prevent selecting dates before the current year (blocks 2025 and below when current year is 2026)
        minDate: moment().startOf('year').format('YYYY-MM-DD'),
        // default to a 7-day window ending yesterday (e.g. Feb 9 - Feb 15 when today is Feb 16) as per request
        defaultDate: [moment().subtract(7, 'days').format('YYYY-MM-DD'), moment().subtract(1, 'days').format('YYYY-MM-DD')],
        locale: {
          rangeSeparator: ' - '
        },
        onChange: (selectedDates) => {
          if (selectedDates.length === 2) {
            this.dateRange.start = moment(selectedDates[0]).format('YYYY-MM-DD');
            this.dateRange.end = moment(selectedDates[1]).format('YYYY-MM-DD');
            this.onDateRangeChange();
          }
        }
      },
      isLoading: false
    };
  },
  computed: {
    ...eloadDashboardComputed,
    
    // Dynamic date labels for table headers
    priorMonthLabel() {
      return moment().subtract(1, 'month').format('MMM YYYY');
    },
    pmMtdLabel() {
      const prevMonth = moment().subtract(1, 'month');
      return `${prevMonth.format('MMM')} 1 - ${moment().format('D')}`;
    },
    mtdLabel() {
      const currentMonth = moment();
      return `${currentMonth.format('MMM')} 1 - ${currentMonth.format('D')}`;
    },
    
    // Calculate Total E-Load values (Total = Load In + Load Out)
    totalEloadMTD() {
      return this.totalLoadInMTD + this.totalLoadOutMTD;
    },
    totalEloadPMMTD() {
      return this.totalLoadInPMMTD + this.totalLoadOutPMMTD;
    },
    totalEloadPM() {
      return this.totalLoadInPM + this.totalLoadOutPM;
    },
    totalEloadPercentageChange() {
      if (this.totalEloadPMMTD === 0) return 0;
      return (((this.totalEloadMTD - this.totalEloadPMMTD) / this.totalEloadPMMTD) * 100);
    },
    
    // Calculate Total Load In
    totalLoadInMTD() {
      return this.eloadSummary.retailLoadInMTD + this.eloadSummary.b2bLoadInMTD;
    },
    totalLoadInPMMTD() {
      return this.eloadSummary.retailLoadInPMMTD + this.eloadSummary.b2bLoadInPMMTD;
    },
    totalLoadInPM() {
      return this.eloadSummary.retailLoadInPM + this.eloadSummary.b2bLoadInPM;
    },
    totalLoadInPercentageChange() {
      if (this.totalLoadInPMMTD === 0) return 0;
      return (((this.totalLoadInMTD - this.totalLoadInPMMTD) / this.totalLoadInPMMTD) * 100);
    },
    
    // Calculate Total Load Out
    totalLoadOutMTD() {
      return this.eloadSummary.retailLoadOutMTD + this.eloadSummary.b2bLoadOutMTD;
    },
    totalLoadOutPMMTD() {
      return this.eloadSummary.retailLoadOutPMMTD + this.eloadSummary.b2bLoadOutPMMTD;
    },
    totalLoadOutPM() {
      return this.eloadSummary.retailLoadOutPM + this.eloadSummary.b2bLoadOutPM;
    },
    totalLoadOutPercentageChange() {
      if (this.totalLoadOutPMMTD === 0) return 0;
      return (((this.totalLoadOutMTD - this.totalLoadOutPMMTD) / this.totalLoadOutPMMTD) * 100);
    },
    
    // Retail Total calculations (Total = Load In + Load Out)
    retailTotalMTD() {
      return this.eloadSummary.retailLoadInMTD + this.eloadSummary.retailLoadOutMTD;
    },
    retailTotalPMMTD() {
      return this.eloadSummary.retailLoadInPMMTD + this.eloadSummary.retailLoadOutPMMTD;
    },
    retailTotalPM() {
      return this.eloadSummary.retailLoadInPM + this.eloadSummary.retailLoadOutPM;
    },
    
    // Retail percentage changes
    retailPercentageChange() {
      const pmmtd = this.retailTotalPMMTD;
      if (pmmtd === 0) return 0;
      return (((this.retailTotalMTD - pmmtd) / pmmtd) * 100);
    },
    retailLoadInPercentageChange() {
      const pmmtd = this.eloadSummary.retailLoadInPMMTD;
      if (pmmtd === 0) return 0;
      return (((this.eloadSummary.retailLoadInMTD - pmmtd) / pmmtd) * 100);
    },
    retailLoadOutPercentageChange() {
      const pmmtd = this.eloadSummary.retailLoadOutPMMTD;
      if (pmmtd === 0) return 0;
      return (((this.eloadSummary.retailLoadOutMTD - pmmtd) / pmmtd) * 100);
    },
    
    // B2B Total calculations (Total = Load In + Load Out)
    b2bTotalMTD() {
      return this.eloadSummary.b2bLoadInMTD + this.eloadSummary.b2bLoadOutMTD;
    },
    b2bTotalPMMTD() {
      return this.eloadSummary.b2bLoadInPMMTD + this.eloadSummary.b2bLoadOutPMMTD;
    },
    b2bTotalPM() {
      return this.eloadSummary.b2bLoadInPM + this.eloadSummary.b2bLoadOutPM;
    },
    
    // B2B percentage changes
    b2bPercentageChange() {
      const pmmtd = this.b2bTotalPMMTD;
      if (pmmtd === 0) return 0;
      return (((this.b2bTotalMTD - pmmtd) / pmmtd) * 100);
    },
    b2bLoadInPercentageChange() {
      const pmmtd = this.eloadSummary.b2bLoadInPMMTD;
      if (pmmtd === 0) return 0;
      return (((this.eloadSummary.b2bLoadInMTD - pmmtd) / pmmtd) * 100);
    },
    b2bLoadOutPercentageChange() {
      const pmmtd = this.eloadSummary.b2bLoadOutPMMTD;
      if (pmmtd === 0) return 0;
      return (((this.eloadSummary.b2bLoadOutMTD - pmmtd) / pmmtd) * 100);
    },
    
    // Total Active Users
    totalActiveUsersMTD() {
      return this.activeUsers.retailActiveUsersMTD + this.activeUsers.b2bActiveUsersMTD;
    },
    totalActiveUsersPMMTD() {
      return this.activeUsers.retailActiveUsersPMMTD + this.activeUsers.b2bActiveUsersPMMTD;
    },
    totalActiveUsersPM() {
      return this.activeUsers.retailActiveUsersPM + this.activeUsers.b2bActiveUsersPM;
    },
    
    // Total CM1
    totalCM1MTD() {
      return this.eloadSummary.retailCM1MTD + this.eloadSummary.b2bCM1MTD;
    },
    totalCM1PMMTD() {
      return this.eloadSummary.retailCM1PMMTD + this.eloadSummary.b2bCM1PMMTD;
    },
    totalCM1PM() {
      return this.eloadSummary.retailCM1PM + this.eloadSummary.b2bCM1PM;
    },
    
    // Total CM2 (Retail + B2B)
    totalCM2MTD() {
      return this.eloadSummary.retailCM2MTD + this.eloadSummary.b2bCM2MTD;
    },
    totalCM2PMMTD() {
      return this.eloadSummary.retailCM2PMMTD + this.eloadSummary.b2bCM2PMMTD;
    },
    totalCM2PM() {
      return this.eloadSummary.retailCM2PM + this.eloadSummary.b2bCM2PM;
    },
    
    // Dynamic date range display for header cards
    currentMonthRange() {
      const start = moment().startOf('month').format('MMMM D');
      const end = moment().subtract(1, 'day').format('D, YYYY');
      return `${start} - ${end}`;
    },
    
    // Format date range for display
    formattedDateRange() {
      return `${moment(this.dateRange.start).format('MMM DD, YYYY')} - ${moment(this.dateRange.end).format('MMM DD, YYYY')}`;
    },
    
    // Daily transactions formatted for table
    dailyTransactionsDates() {
      if (this.dailyTransactionTab === 'peddlr-lite') {
        return this.retailDailyDates;
      } else if (this.dailyTransactionTab === 'b2b') {
        return this.b2bDailyDates;
      } else {
        return this.dailyDates;
      }
    },
    
    // Check if Total E-Load card has millions
    // Determine scale for ALL values based on ONLY the highest Total E-Load total value
    businessSegmentScale() {
      // Check ONLY the 3 Total E-Load TOTAL values (not Load In/Out separately)
      // Total E-Load PM = totalLoadInPM + totalLoadOutPM
      // Total E-Load PMMTD = totalLoadInPMMTD + totalLoadOutPMMTD (x)
      // Total E-Load MTD = totalLoadInMTD + totalLoadOutMTD
      const totalEloadValues = [
        this.totalEloadPM,    // PM Total
        // this.totalEloadPMMTD, // PMMTD Total
        this.totalEloadMTD    // MTD Total
      ];
      
      // Find the highest absolute value among the 3 totals
      const maxValue = Math.max(...totalEloadValues.map(val => Math.abs(val || 0)));
      
      // Determine scale: 'M' for millions, 'K' for thousands, '' for hundreds
      if (maxValue >= 1000000) return 'M';
      if (maxValue >= 1000) return 'K';
      return ''; // hundreds
    }
  },
  
  methods: {
    ...layoutMethods,
    ...eloadDashboardMethods,
    
    formatCurrency(amount) {
      if (amount === 0 || !amount) return '--';
      return new Intl.NumberFormat('en-PH', {
        style: 'currency',
        currency: 'PHP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    },
    // Format number for Daily Transactions with M/K suffix
    formatNumber(number) {
      if (number === 0 || !number) return '--';
      return this.formatValue(number);
    },
    getChangeClass(changeType) {
      return {
        'text-success': changeType === 'positive',
        'text-danger': changeType === 'negative',
        'text-muted': changeType === 'neutral'
      };
    },
    initTooltip(element, title, content) {
      if (element && !this.tooltips[element.id]) {
        const tooltip = new Tooltip(element, {
          title: `<strong>${title}</strong><br>${content}`,
          html: true,
          placement: 'top',
          trigger: 'hover',
          container: 'body'
        });
        this.tooltips[element.id] = tooltip;
      }
    },
    initializeAllTooltips() {
      // Dispose existing tooltips first to prevent duplicates
      const existingTooltips = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
      existingTooltips.forEach(tooltipEl => {
        const tooltipInstance = Tooltip.getInstance(tooltipEl);
        if (tooltipInstance) {
          tooltipInstance.dispose();
        }
      });
      
      // Initialize new tooltips with better positioning
      const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
      tooltipTriggerList.map(function (tooltipTriggerEl) {
        return new Tooltip(tooltipTriggerEl, {
          html: true,
          boundary: 'window',
          container: 'body',
          customClass: 'dashboard-tooltip-left',
          fallbackPlacements: ['top', 'bottom', 'right', 'left']
        });
      });
    },
    // Format tooltip text with "PM MTD" label on top and complete value on bottom
    formatTooltip(value) {
      const completeValue = this.getCompleteValue(value);
      return `
        <div class="dashboard-tooltip-content">
          <div class="dashboard-tooltip-title">PM MTD</div>
          <div class="dashboard-tooltip-value">${completeValue}</div>
        </div>
      `;
    },
    setActiveTab(tab) {
      this.activeTab = tab;
    },
    setSummaryTab(tab) {
      this.summaryTab = tab;
      this.$nextTick(() => {
        this.initializeAllTooltips();
      });
    },
    setDailyTransactionTab(tab) {
      this.dailyTransactionTab = tab;
      this.$nextTick(() => {
        this.initializeAllTooltips();
      });
    },
    
    // Format value for Active Users and Daily Transactions (normal M/K conversion, no 0M/0K)
    formatValue(value) {
      if (value === 0 || !value) return '--';
      
      const absValue = Math.abs(value);
      
      // If in hundreds, show as is
      if (absValue < 1000) {
        return Math.round(value).toString();
      }
      
      // If in thousands, show with K
      if (absValue < 1000000) {
        return Math.floor(value / 1000) + 'K';
      }
      
      // If in millions, show with M
      return Math.floor(value / 1000000) + 'M';
    },
    
    // Format value for Total E-Load card
    formatTotalEloadValue(value) {
      return this.formatBusinessSegmentValue(value);
    },
    
    // Format value for Retail card
    formatRetailValue(value) {
      return this.formatBusinessSegmentValue(value);
    },
    
    // Format value for B2B card
    formatB2BValue(value) {
      return this.formatBusinessSegmentValue(value);
    },
    
    // Format value for Business Segment and Summary P&L tables using the single scale yezsirs
    formatBusinessSegmentValue(value) {
      if (value === 0 || !value) return '--';
      
      const absValue = Math.abs(value);
      const scale = this.businessSegmentScale;
      
      if (scale === 'M') {
        // Millions scale: convert K to 0M, M to M
        if (absValue < 1000000) {
          return '0M';
        }
        return Math.floor(value / 1000000) + 'M';
      } else if (scale === 'K') {
        // Thousands scale: convert hundreds to 0K, K to K
        if (absValue < 1000) {
          return '0K';
        }
        return Math.floor(value / 1000) + 'K';
      } else {
        // Hundreds scale: show as is
        return Math.round(value).toString();
      }
    },
    
    // Get complete value for tooltip (no decimals)
    getCompleteValue(value) {
      if (value === 0 || !value) return '--';
      return new Intl.NumberFormat('en-PH', {
        style: 'currency',
        currency: 'PHP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },
    
    formatPercentage(value) {
      const n = Number(value);
      if (!Number.isFinite(n) || n === 0) return '0%';
      return `${Math.abs(n).toFixed(1)}%`;
    },
    
    getPercentageClass(value) {
      if (value === 0) return 'text-neutral';
      if (value > 0) return 'text-success';
      if (value < 0) return 'text-danger';
      return 'text-muted';
    },
    
    async loadDashboardData() {
      try {
        this.isLoading = true;
        
        // Load summary and active users data
        await Promise.all([
          this.getELoadSummary(),
          this.getActiveUsers()
        ]);
        
        // Load daily transactions
        const dateRangeParam = {
          dateRange: `${this.dateRange.start} to ${this.dateRange.end}`
        };
        
        await Promise.all([
          this.getRetailDailyTransactions(dateRangeParam),
          this.getB2BDailyTransactions(dateRangeParam),
          this.getDailyTransactionsPerProvider(dateRangeParam)
        ]);
        
        this.isLoading = false;
        
        // Reinitialize tooltips after data loads
        this.$nextTick(() => {
          this.initializeAllTooltips();
        });
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        this.isLoading = false;
      }
    },
    
    async onDateRangeChange() {
      const dateRangeParam = {
        dateRange: `${this.dateRange.start} to ${this.dateRange.end}`
      };
      
      try {
        await Promise.all([
          this.getRetailDailyTransactions(dateRangeParam),
          this.getB2BDailyTransactions(dateRangeParam),
          this.getDailyTransactionsPerProvider(dateRangeParam)
        ]);
        
        // Reinitialize tooltips after data updates
        this.$nextTick(() => {
          this.initializeAllTooltips();
        });
      } catch (error) {
        console.error('Error updating date range:', error);
      }
    },

    // Get complete value for Daily Transactions tooltip asd
    getTransactionCompleteValue(date, key) {
      if (this.dailyTransactionTab === 'peddlr-lite') {
        const data = this.retailDailyTransactions[date];
        return data && data[key] !== undefined ? data[key] : '';
      } else if (this.dailyTransactionTab === 'b2b') {
        const data = this.b2bDailyTransactions[date];
        return data && data[key] !== undefined ? data[key] : '';
      }
      return '';
    },
    // Always read from retail data source (used in Peddlr Lite tab tooltips)
    getRetailTransactionCompleteValue(date, key) {
      const data = this.retailDailyTransactions[date];
      if (!data || data[key] === undefined) return '';
      const value = data[key];
      if (value === 0 || !value) return '--';
      if (key === 'uniqueUsers') return new Intl.NumberFormat('en-PH').format(value);
      return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
    },

    // Always read from b2b data source (used in B2B tab tooltips)
    getB2BTransactionCompleteValue(date, key) {
      const data = this.b2bDailyTransactions[date];
      if (!data || data[key] === undefined) return '';
      const value = data[key];
      if (value === 0 || !value) return '--';
      if (key === 'uniqueUsers') return new Intl.NumberFormat('en-PH').format(value);
      return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
    },

    formatCountTooltip(value) {
      if (value === 0 || !value) return '--';
      return new Intl.NumberFormat('en-PH').format(value);
    },
    
    getTransactionValue(date, key) {
      if (this.dailyTransactionTab === 'peddlr-lite') {
        const data = this.retailDailyTransactions[date];
        return data && data[key] !== undefined ? this.formatNumber(data[key]) : '--';
      } else if (this.dailyTransactionTab === 'b2b') {
        const data = this.b2bDailyTransactions[date];
        return data && data[key] !== undefined ? this.formatNumber(data[key]) : '--';
      }
      return '--';
    },
    
    // Get complete value for provider tooltip
    getProviderCompleteValue(providerName, date) {
      const provider = this.dailyTransactionsPerProvider.find(p => p.name === providerName);
      if (provider && provider.result && provider.result[date]) {
        const value = provider.result[date].loadout;
        if (value === undefined) return '';
        if (value === 0 || !value) return '--';
        return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
      }
      return '';
    },
    getB2BProviderCompleteValue(providerName, date) {
      const provider = this.b2bDailyTransactionsPerProvider.find(p => p.name === providerName);
      if (provider && provider.result && provider.result[date]) {
        const value = provider.result[date].loadout;
        if (value === undefined) return '';
        if (value === 0 || !value) return '--';
        return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
      }
      return '';
    },
    getProviderValue(providerName, date) {
      const provider = this.dailyTransactionsPerProvider.find(p => p.name === providerName);
      if (provider && provider.result && provider.result[date]) {
        const value = provider.result[date].loadout;
        return value !== undefined ? this.formatNumber(value) : '--';
      }
      return '--';
    },
    getB2BProviderValue(providerName, date) {
      const provider = this.b2bDailyTransactionsPerProvider.find(p => p.name === providerName);
      if (provider && provider.result && provider.result[date]) {
        const value = provider.result[date].loadout;
        return value !== undefined ? this.formatNumber(value) : '--';
      }
      return '--';
    },
    formatDateHeader(dateString) {
      // Convert date string to proper format (e.g. "Sep 24, 2025")
      const date = moment(dateString);
      return date.format('MMM D, YYYY');
    }
  },
  async mounted() {
    this.changeTitle({ title: "E-Load Dashboard" });
    this.changeBreadCrumb({
      breadCrumbs: [
        { text: "Dashboard", to: { name: "dashboard" } },
        { text: "E-Load", to: { name: "eload-dashboard" } }
      ],
    });
    
    // Load dashboard data
    await this.loadDashboardData();
    
    this.$nextTick(() => {
      this.initializeAllTooltips();
    });
  },
};
</script>

<template>
  <div class="container-fluid">
    <!-- Coming Soon Overlay -->
    <div v-if="comingSoonOverlay" class="coming-soon-overlay">
      <div class="coming-soon-content">
        <div class="text-center">
          <i class="fas fa-rocket fa-4x mb-4 text-primary"></i>
          <h2 class="fw-bold text-dark mb-3">Coming Soon</h2>
          <p class="text-muted mb-0">This E-Load Dashboard is under development and will be available soon.</p>
        </div>
      </div>
    </div>
    
    <!-- Header Cards -->
    <div class="row g-3 mb-4">
      <!-- Total E-Load Card -->
      <div class="col-lg-4 col-md-6">
        <div class="card h-100 border-0 shadow-sm position-relative card-stat">
          <div class="card-body">
            <h6 class="card-title card-label mb-3 fw-normal">Total E-Load</h6>
            <div class="d-flex align-items-center mb-2">
              <h3 class="mb-0 me-2 fw-bold card-value-main">
                <span class="peso-sign-gray">₱</span>{{ formatTotalEloadValue(totalEloadMTD) }}
              </h3>
              <span
                id="eload-percentage"
                data-bs-toggle="tooltip"
                data-bs-placement="right"
                :class="['small fw-medium cursor-pointer d-inline-flex align-items-center rounded px-2 py-1', getPercentageClass(totalEloadPercentageChange), 'bg-opacity-75']"
                :style="{ borderRadius: '4px', background: totalEloadPercentageChange === 0 ? '#F9F9F9' : (totalEloadPercentageChange > 0 ? 'var(--Success-Success-Light, #E8FFF3)' : 'var(--Danger-Danger-Light, #FFE8E8)') }"
                :title="formatTooltip(totalEloadPMMTD)"
              >
                <svg v-if="totalEloadPercentageChange > 0.0" xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none" class="mx-1">
                  <path d="M6.46477 3.9629C6.39897 3.96328 6.33374 3.95067 6.27282 3.92579C6.2119 3.9009 6.15649 3.86424 6.10977 3.8179L3.49477 1.2079L0.879772 3.8229C0.834948 3.87524 0.779789 3.91775 0.717757 3.94776C0.655725 3.97777 0.588158 3.99464 0.519298 3.9973C0.450439 3.99996 0.381773 3.98836 0.31761 3.96322C0.253448 3.93808 0.195174 3.89995 0.146447 3.85122C0.0977193 3.8025 0.0595904 3.74422 0.0344531 3.68006C0.00931581 3.6159 -0.00228716 3.54723 0.00037259 3.47837C0.00303234 3.40951 0.0198974 3.34195 0.0499091 3.27991C0.0799207 3.21788 0.122431 3.16272 0.174772 3.1179L3.13977 0.147899C3.18625 0.101035 3.24155 0.0638379 3.30248 0.0384536C3.36341 0.0130692 3.42877 0 3.49477 0C3.56078 0 3.62613 0.0130692 3.68706 0.0384536C3.74799 0.0638379 3.80329 0.101035 3.84977 0.147899L6.81477 3.1179C6.9079 3.21158 6.96017 3.33831 6.96017 3.4704C6.96017 3.60249 6.9079 3.72922 6.81477 3.8229C6.72084 3.91344 6.59524 3.96368 6.46477 3.9629Z" fill="#50CD89"/>
                  <path d="M3.49463 6.96293C3.36202 6.96293 3.23484 6.91025 3.14108 6.81648C3.04731 6.72272 2.99463 6.59554 2.99463 6.46293V0.50293C2.99463 0.370321 3.04731 0.243145 3.14108 0.149376C3.23484 0.0556082 3.36202 0.00292969 3.49463 0.00292969C3.62724 0.00292969 3.75441 0.0556082 3.84818 0.149376C3.94195 0.243145 3.99463 0.370321 3.99463 0.50293V6.46293C3.99463 6.59554 3.94195 6.72272 3.84818 6.81648C3.75441 6.91025 3.62724 6.96293 3.49463 6.96293Z" fill="#50CD89"/>
                </svg>
                <svg v-else-if="totalEloadPercentageChange < 0.0" xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none" class="mx-1">
                  <path d="M3.49477 6.95996C3.42897 6.96034 3.36374 6.94773 3.30282 6.92284C3.2419 6.89796 3.18649 6.8613 3.13977 6.81496L0.174772 3.81996C0.122431 3.77513 0.0799207 3.71997 0.0499091 3.65794C0.0198974 3.59591 0.00303234 3.52834 0.00037259 3.45948C-0.00228716 3.39062 0.00931581 3.32196 0.0344531 3.2578C0.0595904 3.19363 0.0977193 3.13536 0.146447 3.08663C0.195174 3.0379 0.253448 2.99978 0.31761 2.97464C0.381773 2.9495 0.450439 2.9379 0.519299 2.94056C0.588158 2.94322 0.655725 2.96008 0.717757 2.99009C0.779789 3.02011 0.834948 3.06262 0.879772 3.11496L3.49477 5.72996L6.10977 3.11496C6.1546 3.06262 6.20975 3.02011 6.27179 2.99009C6.33382 2.96008 6.40139 2.94322 6.47025 2.94056C6.53911 2.9379 6.60777 2.9495 6.67193 2.97464C6.7361 2.99978 6.79437 3.0379 6.8431 3.08663C6.89182 3.13536 6.92995 3.19363 6.95509 3.2578C6.98023 3.32196 6.99183 3.39062 6.98917 3.45948C6.98651 3.52834 6.96965 3.59591 6.93964 3.65794C6.90962 3.71997 6.86711 3.77513 6.81477 3.81996L3.84977 6.78996C3.80518 6.84084 3.75072 6.88214 3.6897 6.91136C3.62868 6.94058 3.56237 6.95711 3.49477 6.95996Z" fill="#D9214E"/>
                  <path d="M3.49463 6.96C3.36202 6.96 3.23484 6.90732 3.14108 6.81355C3.04731 6.71979 2.99463 6.59261 2.99463 6.46V0.5C2.99463 0.367392 3.04731 0.240215 3.14108 0.146447C3.23484 0.0526785 3.36202 0 3.49463 0C3.62724 0 3.75441 0.0526785 3.84818 0.146447C3.94195 0.240215 3.99463 0.367392 3.99463 0.5V6.46C3.99463 6.59261 3.94195 6.71979 3.84818 6.81355C3.75441 6.90732 3.62724 6.96 3.49463 6.96Z" fill="#D9214E"/>
                </svg>
                {{ formatPercentage(totalEloadPercentageChange) }}
              </span>
            </div>
            <p class="small card-date mb-3">{{ currentMonthRange }}</p>
            
            <div class="border-top pt-3 mt-3">
              <div class="d-flex justify-content-between align-items-center">
                <span class="medium card-label">Load In</span>
                <div class="d-flex align-items-center">
                  <span class="fw-bold card-value-dark">
                    ₱{{ formatTotalEloadValue(totalLoadInMTD) }}
                  </span>
                  <span :class="['small fw-medium cursor-pointer d-inline-flex align-items-center rounded percentage-badge-small', getPercentageClass(totalLoadInPercentageChange)]" data-bs-toggle="tooltip" data-bs-placement="right" :title="formatTooltip(totalLoadInPMMTD)">
                    <svg v-if="totalLoadInPercentageChange > 0.0" xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                      <path d="M6.46477 3.9629C6.39897 3.96328 6.33374 3.95067 6.27282 3.92579C6.2119 3.9009 6.15649 3.86424 6.10977 3.8179L3.49477 1.2079L0.879772 3.8229C0.834948 3.87524 0.779789 3.91775 0.717757 3.94776C0.655725 3.97777 0.588158 3.99464 0.519298 3.9973C0.450439 3.99996 0.381773 3.98836 0.31761 3.96322C0.253448 3.93808 0.195174 3.89995 0.146447 3.85122C0.0977193 3.8025 0.0595904 3.74422 0.0344531 3.68006C0.00931581 3.6159 -0.00228716 3.54723 0.00037259 3.47837C0.00303234 3.40951 0.0198974 3.34195 0.0499091 3.27991C0.0799207 3.21788 0.122431 3.16272 0.174772 3.1179L3.13977 0.147899C3.18625 0.101035 3.24155 0.0638379 3.30248 0.0384536C3.36341 0.0130692 3.42877 0 3.49477 0C3.56078 0 3.62613 0.0130692 3.68706 0.0384536C3.74799 0.0638379 3.80329 0.101035 3.84977 0.147899L6.81477 3.1179C6.9079 3.21158 6.96017 3.33831 6.96017 3.4704C6.96017 3.60249 6.9079 3.72922 6.81477 3.8229C6.72084 3.91344 6.59524 3.96368 6.46477 3.9629Z" fill="#50CD89"/>
                      <path d="M3.49463 6.96293C3.36202 6.96293 3.23484 6.91025 3.14108 6.81648C3.04731 6.72272 2.99463 6.59554 2.99463 6.46293V0.50293C2.99463 0.370321 3.04731 0.243145 3.14108 0.149376C3.23484 0.0556082 3.36202 0.00292969 3.49463 0.00292969C3.62724 0.00292969 3.75441 0.0556082 3.84818 0.149376C3.94195 0.243145 3.99463 0.370321 3.99463 0.50293V6.46293C3.99463 6.59554 3.94195 6.72272 3.84818 6.81648C3.75441 6.91025 3.62724 6.96293 3.49463 6.96293Z" fill="#50CD89"/>
                    </svg>
                    <svg v-else-if="totalLoadInPercentageChange < 0.0" xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                      <path d="M3.49477 6.95996C3.42897 6.96034 3.36374 6.94773 3.30282 6.92284C3.2419 6.89796 3.18649 6.8613 3.13977 6.81496L0.174772 3.81996C0.122431 3.77513 0.0799207 3.71997 0.0499091 3.65794C0.0198974 3.59591 0.00303234 3.52834 0.00037259 3.45948C-0.00228716 3.39062 0.00931581 3.32196 0.0344531 3.2578C0.0595904 3.19363 0.0977193 3.13536 0.146447 3.08663C0.195174 3.0379 0.253448 2.99978 0.31761 2.97464C0.381773 2.9495 0.450439 2.9379 0.519299 2.94056C0.588158 2.94322 0.655725 2.96008 0.717757 2.99009C0.779789 3.02011 0.834948 3.06262 0.879772 3.11496L3.49477 5.72996L6.10977 3.11496C6.1546 3.06262 6.20975 3.02011 6.27179 2.99009C6.33382 2.96008 6.40139 2.94322 6.47025 2.94056C6.53911 2.9379 6.60777 2.9495 6.67193 2.97464C6.7361 2.99978 6.79437 3.0379 6.8431 3.08663C6.89182 3.13536 6.92995 3.19363 6.95509 3.2578C6.98023 3.32196 6.99183 3.39062 6.98917 3.45948C6.98651 3.52834 6.96965 3.59591 6.93964 3.65794C6.90962 3.71997 6.86711 3.77513 6.81477 3.81996L3.84977 6.78996C3.80518 6.84084 3.75072 6.88214 3.6897 6.91136C3.62868 6.94058 3.56237 6.95711 3.49477 6.95996Z" fill="#D9214E"/>
                      <path d="M3.49463 6.96C3.36202 6.96 3.23484 6.90732 3.14108 6.81355C3.04731 6.71979 2.99463 6.59261 2.99463 6.46V0.5C2.99463 0.367392 3.04731 0.240215 3.14108 0.146447C3.23484 0.0526785 3.36202 0 3.49463 0C3.62724 0 3.75441 0.0526785 3.84818 0.146447C3.94195 0.240215 3.99463 0.367392 3.99463 0.5V6.46C3.99463 6.59261 3.94195 6.71979 3.84818 6.81355C3.75441 6.90732 3.62724 6.96 3.49463 6.96Z" fill="#D9214E"/>
                    </svg>
                    {{ formatPercentage(totalLoadInPercentageChange) }}
                  </span>
                </div>
              </div>
              <div class="d-flex justify-content-between align-items-center">
                <span class="medium card-label">Load Out</span>
                <div class="d-flex align-items-center">
                  <span class="fw-bold card-value-dark">
                    ₱{{ formatTotalEloadValue(totalLoadOutMTD) }}
                  </span>
                  <span :class="['small fw-medium cursor-pointer d-inline-flex align-items-center rounded percentage-badge-small', getPercentageClass(totalLoadOutPercentageChange)]" data-bs-toggle="tooltip" data-bs-placement="right" :title="formatTooltip(totalLoadOutPMMTD)">
                    <svg v-if="totalLoadOutPercentageChange > 0.0" xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                      <path d="M6.46477 3.9629C6.39897 3.96328 6.33374 3.95067 6.27282 3.92579C6.2119 3.9009 6.15649 3.86424 6.10977 3.8179L3.49477 1.2079L0.879772 3.8229C0.834948 3.87524 0.779789 3.91775 0.717757 3.94776C0.655725 3.97777 0.588158 3.99464 0.519298 3.9973C0.450439 3.99996 0.381773 3.98836 0.31761 3.96322C0.253448 3.93808 0.195174 3.89995 0.146447 3.85122C0.0977193 3.8025 0.0595904 3.74422 0.0344531 3.68006C0.00931581 3.6159 -0.00228716 3.54723 0.00037259 3.47837C0.00303234 3.40951 0.0198974 3.34195 0.0499091 3.27991C0.0799207 3.21788 0.122431 3.16272 0.174772 3.1179L3.13977 0.147899C3.18625 0.101035 3.24155 0.0638379 3.30248 0.0384536C3.36341 0.0130692 3.42877 0 3.49477 0C3.56078 0 3.62613 0.0130692 3.68706 0.0384536C3.74799 0.0638379 3.80329 0.101035 3.84977 0.147899L6.81477 3.1179C6.9079 3.21158 6.96017 3.33831 6.96017 3.4704C6.96017 3.60249 6.9079 3.72922 6.81477 3.8229C6.72084 3.91344 6.59524 3.96368 6.46477 3.9629Z" fill="#50CD89"/>
                      <path d="M3.49463 6.96293C3.36202 6.96293 3.23484 6.91025 3.14108 6.81648C3.04731 6.72272 2.99463 6.59554 2.99463 6.46293V0.50293C2.99463 0.370321 3.04731 0.243145 3.14108 0.149376C3.23484 0.0556082 3.36202 0.00292969 3.49463 0.00292969C3.62724 0.00292969 3.75441 0.0556082 3.84818 0.149376C3.94195 0.243145 3.99463 0.370321 3.99463 0.50293V6.46293C3.99463 6.59554 3.94195 6.72272 3.84818 6.81648C3.75441 6.91025 3.62724 6.96293 3.49463 6.96293Z" fill="#50CD89"/>
                    </svg>
                    <svg v-else-if="totalLoadOutPercentageChange < 0.0" xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                      <path d="M3.49477 6.95996C3.42897 6.96034 3.36374 6.94773 3.30282 6.92284C3.2419 6.89796 3.18649 6.8613 3.13977 6.81496L0.174772 3.81996C0.122431 3.77513 0.0799207 3.71997 0.0499091 3.65794C0.0198974 3.59591 0.00303234 3.52834 0.00037259 3.45948C-0.00228716 3.39062 0.00931581 3.32196 0.0344531 3.2578C0.0595904 3.19363 0.0977193 3.13536 0.146447 3.08663C0.195174 3.0379 0.253448 2.99978 0.31761 2.97464C0.381773 2.9495 0.450439 2.9379 0.519299 2.94056C0.588158 2.94322 0.655725 2.96008 0.717757 2.99009C0.779789 3.02011 0.834948 3.06262 0.879772 3.11496L3.49477 5.72996L6.10977 3.11496C6.1546 3.06262 6.20975 3.02011 6.27179 2.99009C6.33382 2.96008 6.40139 2.94322 6.47025 2.94056C6.53911 2.9379 6.60777 2.9495 6.67193 2.97464C6.7361 2.99978 6.79437 3.0379 6.8431 3.08663C6.89182 3.13536 6.92995 3.19363 6.95509 3.2578C6.98023 3.32196 6.99183 3.39062 6.98917 3.45948C6.98651 3.52834 6.96965 3.59591 6.93964 3.65794C6.90962 3.71997 6.86711 3.77513 6.81477 3.81996L3.84977 6.78996C3.80518 6.84084 3.75072 6.88214 3.6897 6.91136C3.62868 6.94058 3.56237 6.95711 3.49477 6.95996Z" fill="#D9214E"/>
                      <path d="M3.49463 6.96C3.36202 6.96 3.23484 6.90732 3.14108 6.81355C3.04731 6.71979 2.99463 6.59261 2.99463 6.46V0.5C2.99463 0.367392 3.04731 0.240215 3.14108 0.146447C3.23484 0.0526785 3.36202 0 3.49463 0C3.62724 0 3.75441 0.0526785 3.84818 0.146447C3.94195 0.240215 3.99463 0.367392 3.99463 0.5V6.46C3.99463 6.59261 3.94195 6.71979 3.84818 6.81355C3.75441 6.90732 3.62724 6.96 3.49463 6.96Z" fill="#D9214E"/>
                    </svg>
                    {{ formatPercentage(totalLoadOutPercentageChange) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Retail Card -->
      <div class="col-lg-4 col-md-6">
        <div class="card h-100 border-0 shadow-sm position-relative">
          <div class="card-body">
            <h6 class="card-title card-label mb-3 fw-normal">Retail</h6>
            <div class="d-flex align-items-center mb-2">
              <h3 class="mb-0 me-2 fw-bold card-value-main">
                <span class="peso-sign-gray">₱</span>{{ formatBusinessSegmentValue(retailTotalMTD) }}
              </h3>
              <span
                id="retail-percentage"
                data-bs-toggle="tooltip"
                data-bs-placement="right"
                :class="['small fw-medium cursor-pointer d-inline-flex align-items-center rounded px-2 py-1', getPercentageClass(retailPercentageChange), 'bg-opacity-75']"
                :style="{ borderRadius: '4px', background: retailPercentageChange === 0 ? '#F9F9F9' : (retailPercentageChange > 0 ? 'var(--Success-Success-Light, #E8FFF3)' : 'var(--Danger-Danger-Light, #FFE8E8)') }"
                :title="formatTooltip(retailTotalPMMTD)"
              >
                <svg v-if="retailPercentageChange > 0" xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none" class="mx-1">
                    <path d="M6.46477 3.9629C6.39897 3.96328 6.33374 3.95067 6.27282 3.92579C6.2119 3.9009 6.15649 3.86424 6.10977 3.8179L3.49477 1.2079L0.879772 3.8229C0.834948 3.87524 0.779789 3.91775 0.717757 3.94776C0.655725 3.97777 0.588158 3.99464 0.519298 3.9973C0.450439 3.99996 0.381773 3.98836 0.31761 3.96322C0.253448 3.93808 0.195174 3.89995 0.146447 3.85122C0.0977193 3.8025 0.0595904 3.74422 0.0344531 3.68006C0.00931581 3.6159 -0.00228716 3.54723 0.00037259 3.47837C0.00303234 3.40951 0.0198974 3.34195 0.0499091 3.27991C0.0799207 3.21788 0.122431 3.16272 0.174772 3.1179L3.13977 0.147899C3.18625 0.101035 3.24155 0.0638379 3.30248 0.0384536C3.36341 0.0130692 3.42877 0 3.49477 0C3.56078 0 3.62613 0.0130692 3.68706 0.0384536C3.74799 0.0638379 3.80329 0.101035 3.84977 0.147899L6.81477 3.1179C6.9079 3.21158 6.96017 3.33831 6.96017 3.4704C6.96017 3.60249 6.9079 3.72922 6.81477 3.8229C6.72084 3.91344 6.59524 3.96368 6.46477 3.9629Z" fill="#50CD89"/>
                    <path d="M3.49463 6.96293C3.36202 6.96293 3.23484 6.91025 3.14108 6.81648C3.04731 6.72272 2.99463 6.59554 2.99463 6.46293V0.50293C2.99463 0.370321 3.04731 0.243145 3.14108 0.149376C3.23484 0.0556082 3.36202 0.00292969 3.49463 0.00292969C3.62724 0.00292969 3.75441 0.0556082 3.84818 0.149376C3.94195 0.243145 3.99463 0.370321 3.99463 0.50293V6.46293C3.99463 6.59554 3.94195 6.72272 3.84818 6.81648C3.75441 6.91025 3.62724 6.96293 3.49463 6.96293Z" fill="#50CD89"/>
                </svg>
                <svg v-else-if="retailPercentageChange < 0.0" xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none" class="mx-1">
                    <path d="M3.49477 6.95996C3.42897 6.96034 3.36374 6.94773 3.30282 6.92284C3.2419 6.89796 3.18649 6.8613 3.13977 6.81496L0.174772 3.81996C0.122431 3.77513 0.0799207 3.71997 0.0499091 3.65794C0.0198974 3.59591 0.00303234 3.52834 0.00037259 3.45948C-0.00228716 3.39062 0.00931581 3.32196 0.0344531 3.2578C0.0595904 3.19363 0.0977193 3.13536 0.146447 3.08663C0.195174 3.0379 0.253448 2.99978 0.31761 2.97464C0.381773 2.9495 0.450439 2.9379 0.519299 2.94056C0.588158 2.94322 0.655725 2.96008 0.717757 2.99009C0.779789 3.02011 0.834948 3.06262 0.879772 3.11496L3.49477 5.72996L6.10977 3.11496C6.1546 3.06262 6.20975 3.02011 6.27179 2.99009C6.33382 2.96008 6.40139 2.94322 6.47025 2.94056C6.53911 2.9379 6.60777 2.9495 6.67193 2.97464C6.7361 2.99978 6.79437 3.0379 6.8431 3.08663C6.89182 3.13536 6.92995 3.19363 6.95509 3.2578C6.98023 3.32196 6.99183 3.39062 6.98917 3.45948C6.98651 3.52834 6.96965 3.59591 6.93964 3.65794C6.90962 3.71997 6.86711 3.77513 6.81477 3.81996L3.84977 6.78996C3.80518 6.84084 3.75072 6.88214 3.6897 6.91136C3.62868 6.94058 3.56237 6.95711 3.49477 6.95996Z" fill="#D9214E"/>
                    <path d="M3.49463 6.96C3.36202 6.96 3.23484 6.90732 3.14108 6.81355C3.04731 6.71979 2.99463 6.59261 2.99463 6.46V0.5C2.99463 0.367392 3.04731 0.240215 3.14108 0.146447C3.23484 0.0526785 3.36202 0 3.49463 0C3.62724 0 3.75441 0.0526785 3.84818 0.146447C3.94195 0.240215 3.99463 0.367392 3.99463 0.5V6.46C3.99463 6.59261 3.94195 6.71979 3.84818 6.81355C3.75441 6.90732 3.62724 6.96 3.49463 6.96Z" fill="#D9214E"/>
                </svg>
                {{ formatPercentage(retailPercentageChange) }}
              </span>
            </div>
            <p class="small card-date mb-3">{{ currentMonthRange }}</p>
            
            <div class="border-top pt-3 mt-3">
              <div class="d-flex justify-content-between align-items-center">
                <span class="medium card-label">Load In</span>
                <div class="d-flex align-items-center">
                  <span class="fw-bold card-value-dark">
                    ₱{{ formatBusinessSegmentValue(eloadSummary.retailLoadInMTD) }}
                  </span>
                  <span :class="['small fw-medium cursor-pointer d-inline-flex align-items-center rounded percentage-badge-small', getPercentageClass(retailLoadInPercentageChange)]" data-bs-toggle="tooltip" data-bs-placement="right" :title="formatTooltip(eloadSummary.retailLoadInPMMTD)">
                    <svg v-if="retailLoadInPercentageChange > 0.0" xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                      <path d="M6.46477 3.9629C6.39897 3.96328 6.33374 3.95067 6.27282 3.92579C6.2119 3.9009 6.15649 3.86424 6.10977 3.8179L3.49477 1.2079L0.879772 3.8229C0.834948 3.87524 0.779789 3.91775 0.717757 3.94776C0.655725 3.97777 0.588158 3.99464 0.519298 3.9973C0.450439 3.99996 0.381773 3.98836 0.31761 3.96322C0.253448 3.93808 0.195174 3.89995 0.146447 3.85122C0.0977193 3.8025 0.0595904 3.74422 0.0344531 3.68006C0.00931581 3.6159 -0.00228716 3.54723 0.00037259 3.47837C0.00303234 3.40951 0.0198974 3.34195 0.0499091 3.27991C0.0799207 3.21788 0.122431 3.16272 0.174772 3.1179L3.13977 0.147899C3.18625 0.101035 3.24155 0.0638379 3.30248 0.0384536C3.36341 0.0130692 3.42877 0 3.49477 0C3.56078 0 3.62613 0.0130692 3.68706 0.0384536C3.74799 0.0638379 3.80329 0.101035 3.84977 0.147899L6.81477 3.1179C6.9079 3.21158 6.96017 3.33831 6.96017 3.4704C6.96017 3.60249 6.9079 3.72922 6.81477 3.8229C6.72084 3.91344 6.59524 3.96368 6.46477 3.9629Z" fill="#50CD89"/>
                      <path d="M3.49463 6.96293C3.36202 6.96293 3.23484 6.91025 3.14108 6.81648C3.04731 6.72272 2.99463 6.59554 2.99463 6.46293V0.50293C2.99463 0.370321 3.04731 0.243145 3.14108 0.149376C3.23484 0.0556082 3.36202 0.00292969 3.49463 0.00292969C3.62724 0.00292969 3.75441 0.0556082 3.84818 0.149376C3.94195 0.243145 3.99463 0.370321 3.99463 0.50293V6.46293C3.99463 6.59554 3.94195 6.72272 3.84818 6.81648C3.75441 6.91025 3.62724 6.96293 3.49463 6.96293Z" fill="#50CD89"/>
                    </svg>
                    <svg v-else-if="retailLoadInPercentageChange < 0.0" xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                      <path d="M3.49477 6.95996C3.42897 6.96034 3.36374 6.94773 3.30282 6.92284C3.2419 6.89796 3.18649 6.8613 3.13977 6.81496L0.174772 3.81996C0.122431 3.77513 0.0799207 3.71997 0.0499091 3.65794C0.0198974 3.59591 0.00303234 3.52834 0.00037259 3.45948C-0.00228716 3.39062 0.00931581 3.32196 0.0344531 3.2578C0.0595904 3.19363 0.0977193 3.13536 0.146447 3.08663C0.195174 3.0379 0.253448 2.99978 0.31761 2.97464C0.381773 2.9495 0.450439 2.9379 0.519299 2.94056C0.588158 2.94322 0.655725 2.96008 0.717757 2.99009C0.779789 3.02011 0.834948 3.06262 0.879772 3.11496L3.49477 5.72996L6.10977 3.11496C6.1546 3.06262 6.20975 3.02011 6.27179 2.99009C6.33382 2.96008 6.40139 2.94322 6.47025 2.94056C6.53911 2.9379 6.60777 2.9495 6.67193 2.97464C6.7361 2.99978 6.79437 3.0379 6.8431 3.08663C6.89182 3.13536 6.92995 3.19363 6.95509 3.2578C6.98023 3.32196 6.99183 3.39062 6.98917 3.45948C6.98651 3.52834 6.96965 3.59591 6.93964 3.65794C6.90962 3.71997 6.86711 3.77513 6.81477 3.81996L3.84977 6.78996C3.80518 6.84084 3.75072 6.88214 3.6897 6.91136C3.62868 6.94058 3.56237 6.95711 3.49477 6.95996Z" fill="#D9214E"/>
                      <path d="M3.49463 6.96C3.36202 6.96 3.23484 6.90732 3.14108 6.81355C3.04731 6.71979 2.99463 6.59261 2.99463 6.46V0.5C2.99463 0.367392 3.04731 0.240215 3.14108 0.146447C3.23484 0.0526785 3.36202 0 3.49463 0C3.62724 0 3.75441 0.0526785 3.84818 0.146447C3.94195 0.240215 3.99463 0.367392 3.99463 0.5V6.46C3.99463 6.59261 3.94195 6.71979 3.84818 6.81355C3.75441 6.90732 3.62724 6.96 3.49463 6.96Z" fill="#D9214E"/>
                    </svg>
                    {{ formatPercentage(retailLoadInPercentageChange) }}
                  </span>
                </div>
              </div>
              <div class="d-flex justify-content-between align-items-center">
                <span class="medium card-label">Load Out</span>
                <div class="d-flex align-items-center">
                  <span class="fw-bold card-value-dark">
                    ₱{{ formatBusinessSegmentValue(eloadSummary.retailLoadOutMTD) }}
                  </span>
                  <span :class="['small fw-medium cursor-pointer d-inline-flex align-items-center rounded percentage-badge-small', getPercentageClass(retailLoadOutPercentageChange)]" data-bs-toggle="tooltip" data-bs-placement="right" :title="formatTooltip(eloadSummary.retailLoadOutPMMTD)">
                    <svg v-if="retailLoadOutPercentageChange > 0.0" xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                      <path d="M6.46477 3.9629C6.39897 3.96328 6.33374 3.95067 6.27282 3.92579C6.2119 3.9009 6.15649 3.86424 6.10977 3.8179L3.49477 1.2079L0.879772 3.8229C0.834948 3.87524 0.779789 3.91775 0.717757 3.94776C0.655725 3.97777 0.588158 3.99464 0.519298 3.9973C0.450439 3.99996 0.381773 3.98836 0.31761 3.96322C0.253448 3.93808 0.195174 3.89995 0.146447 3.85122C0.0977193 3.8025 0.0595904 3.74422 0.0344531 3.68006C0.00931581 3.6159 -0.00228716 3.54723 0.00037259 3.47837C0.00303234 3.40951 0.0198974 3.34195 0.0499091 3.27991C0.0799207 3.21788 0.122431 3.16272 0.174772 3.1179L3.13977 0.147899C3.18625 0.101035 3.24155 0.0638379 3.30248 0.0384536C3.36341 0.0130692 3.42877 0 3.49477 0C3.56078 0 3.62613 0.0130692 3.68706 0.0384536C3.74799 0.0638379 3.80329 0.101035 3.84977 0.147899L6.81477 3.1179C6.9079 3.21158 6.96017 3.33831 6.96017 3.4704C6.96017 3.60249 6.9079 3.72922 6.81477 3.8229C6.72084 3.91344 6.59524 3.96368 6.46477 3.9629Z" fill="#50CD89"/>
                      <path d="M3.49463 6.96293C3.36202 6.96293 3.23484 6.91025 3.14108 6.81648C3.04731 6.72272 2.99463 6.59554 2.99463 6.46293V0.50293C2.99463 0.370321 3.04731 0.243145 3.14108 0.149376C3.23484 0.0556082 3.36202 0.00292969 3.49463 0.00292969C3.62724 0.00292969 3.75441 0.0556082 3.84818 0.149376C3.94195 0.243145 3.99463 0.370321 3.99463 0.50293V6.46293C3.99463 6.59554 3.94195 6.72272 3.84818 6.81648C3.75441 6.91025 3.62724 6.96293 3.49463 6.96293Z" fill="#50CD89"/>
                    </svg>
                    <svg v-else-if="retailLoadOutPercentageChange < 0.0" xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                      <path d="M3.49477 6.95996C3.42897 6.96034 3.36374 6.94773 3.30282 6.92284C3.2419 6.89796 3.18649 6.8613 3.13977 6.81496L0.174772 3.81996C0.122431 3.77513 0.0799207 3.71997 0.0499091 3.65794C0.0198974 3.59591 0.00303234 3.52834 0.00037259 3.45948C-0.00228716 3.39062 0.00931581 3.32196 0.0344531 3.2578C0.0595904 3.19363 0.0977193 3.13536 0.146447 3.08663C0.195174 3.0379 0.253448 2.99978 0.31761 2.97464C0.381773 2.9495 0.450439 2.9379 0.519299 2.94056C0.588158 2.94322 0.655725 2.96008 0.717757 2.99009C0.779789 3.02011 0.834948 3.06262 0.879772 3.11496L3.49477 5.72996L6.10977 3.11496C6.1546 3.06262 6.20975 3.02011 6.27179 2.99009C6.33382 2.96008 6.40139 2.94322 6.47025 2.94056C6.53911 2.9379 6.60777 2.9495 6.67193 2.97464C6.7361 2.99978 6.79437 3.0379 6.8431 3.08663C6.89182 3.13536 6.92995 3.19363 6.95509 3.2578C6.98023 3.32196 6.99183 3.39062 6.98917 3.45948C6.98651 3.52834 6.96965 3.59591 6.93964 3.65794C6.90962 3.71997 6.86711 3.77513 6.81477 3.81996L3.84977 6.78996C3.80518 6.84084 3.75072 6.88214 3.6897 6.91136C3.62868 6.94058 3.56237 6.95711 3.49477 6.95996Z" fill="#D9214E"/>
                      <path d="M3.49463 6.96C3.36202 6.96 3.23484 6.90732 3.14108 6.81355C3.04731 6.71979 2.99463 6.59261 2.99463 6.46V0.5C2.99463 0.367392 3.04731 0.240215 3.14108 0.146447C3.23484 0.0526785 3.36202 0 3.49463 0C3.62724 0 3.75441 0.0526785 3.84818 0.146447C3.94195 0.240215 3.99463 0.367392 3.99463 0.5V6.46C3.99463 6.59261 3.94195 6.71979 3.84818 6.81355C3.75441 6.90732 3.62724 6.96 3.49463 6.96Z" fill="#D9214E"/>
                    </svg>
                    {{ formatPercentage(retailLoadOutPercentageChange) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- B2B Card -->
      <div class="col-lg-4 col-md-6">
        <div class="card h-100 border-0 shadow-sm position-relative">
          <div class="card-body">
            <h6 class="card-title card-label mb-3 fw-normal">B2B</h6>
            <div class="d-flex align-items-center mb-2">
              <h3 class="mb-0 me-2 fw-bold card-value-main">
                <span class="peso-sign-gray">₱</span>{{ formatBusinessSegmentValue(b2bTotalMTD) }}
              </h3>
              <span
                id="b2b-percentage"
                data-bs-toggle="tooltip"
                data-bs-placement="right"
                :class="['small fw-medium cursor-pointer d-inline-flex align-items-center rounded px-2 py-1', getPercentageClass(b2bPercentageChange), 'bg-opacity-75']"
                :style="{ borderRadius: '4px', background: b2bPercentageChange === 0 ? '#F9F9F9' : (b2bPercentageChange > 0 ? 'var(--Success-Success-Light, #E8FFF3)' : 'var(--Danger-Danger-Light, #FFE8E8)') }"
                :title="formatTooltip(b2bTotalPMMTD)"
              >
                <svg v-if="b2bPercentageChange > 0.0" xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none" class="mx-1">
                    <path d="M6.46477 3.9629C6.39897 3.96328 6.33374 3.95067 6.27282 3.92579C6.2119 3.9009 6.15649 3.86424 6.10977 3.8179L3.49477 1.2079L0.879772 3.8229C0.834948 3.87524 0.779789 3.91775 0.717757 3.94776C0.655725 3.97777 0.588158 3.99464 0.519298 3.9973C0.450439 3.99996 0.381773 3.98836 0.31761 3.96322C0.253448 3.93808 0.195174 3.89995 0.146447 3.85122C0.0977193 3.8025 0.0595904 3.74422 0.0344531 3.68006C0.00931581 3.6159 -0.00228716 3.54723 0.00037259 3.47837C0.00303234 3.40951 0.0198974 3.34195 0.0499091 3.27991C0.0799207 3.21788 0.122431 3.16272 0.174772 3.1179L3.13977 0.147899C3.18625 0.101035 3.24155 0.0638379 3.30248 0.0384536C3.36341 0.0130692 3.42877 0 3.49477 0C3.56078 0 3.62613 0.0130692 3.68706 0.0384536C3.74799 0.0638379 3.80329 0.101035 3.84977 0.147899L6.81477 3.1179C6.9079 3.21158 6.96017 3.33831 6.96017 3.4704C6.96017 3.60249 6.9079 3.72922 6.81477 3.8229C6.72084 3.91344 6.59524 3.96368 6.46477 3.9629Z" fill="#50CD89"/>
                    <path d="M3.49463 6.96293C3.36202 6.96293 3.23484 6.91025 3.14108 6.81648C3.04731 6.72272 2.99463 6.59554 2.99463 6.46293V0.50293C2.99463 0.370321 3.04731 0.243145 3.14108 0.149376C3.23484 0.0556082 3.36202 0.00292969 3.49463 0.00292969C3.62724 0.00292969 3.75441 0.0556082 3.84818 0.149376C3.94195 0.243145 3.99463 0.370321 3.99463 0.50293V6.46293C3.99463 6.59554 3.94195 6.72272 3.84818 6.81648C3.75441 6.91025 3.62724 6.96293 3.49463 6.96293Z" fill="#50CD89"/>
                </svg>
                <svg v-else-if="b2bPercentageChange < 0.0" xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none" class="mx-1">
                    <path d="M3.49477 6.95996C3.42897 6.96034 3.36374 6.94773 3.30282 6.92284C3.2419 6.89796 3.18649 6.8613 3.13977 6.81496L0.174772 3.81996C0.122431 3.77513 0.0799207 3.71997 0.0499091 3.65794C0.0198974 3.59591 0.00303234 3.52834 0.00037259 3.45948C-0.00228716 3.39062 0.00931581 3.32196 0.0344531 3.2578C0.0595904 3.19363 0.0977193 3.13536 0.146447 3.08663C0.195174 3.0379 0.253448 2.99978 0.31761 2.97464C0.381773 2.9495 0.450439 2.9379 0.519299 2.94056C0.588158 2.94322 0.655725 2.96008 0.717757 2.99009C0.779789 3.02011 0.834948 3.06262 0.879772 3.11496L3.49477 5.72996L6.10977 3.11496C6.1546 3.06262 6.20975 3.02011 6.27179 2.99009C6.33382 2.96008 6.40139 2.94322 6.47025 2.94056C6.53911 2.9379 6.60777 2.9495 6.67193 2.97464C6.7361 2.99978 6.79437 3.0379 6.8431 3.08663C6.89182 3.13536 6.92995 3.19363 6.95509 3.2578C6.98023 3.32196 6.99183 3.39062 6.98917 3.45948C6.98651 3.52834 6.96965 3.59591 6.93964 3.65794C6.90962 3.71997 6.86711 3.77513 6.81477 3.81996L3.84977 6.78996C3.80518 6.84084 3.75072 6.88214 3.6897 6.91136C3.62868 6.94058 3.56237 6.95711 3.49477 6.95996Z" fill="#D9214E"/>
                    <path d="M3.49463 6.96C3.36202 6.96 3.23484 6.90732 3.14108 6.81355C3.04731 6.71979 2.99463 6.59261 2.99463 6.46V0.5C2.99463 0.367392 3.04731 0.240215 3.14108 0.146447C3.23484 0.0526785 3.36202 0 3.49463 0C3.62724 0 3.75441 0.0526785 3.84818 0.146447C3.94195 0.240215 3.99463 0.367392 3.99463 0.5V6.46C3.99463 6.59261 3.94195 6.71979 3.84818 6.81355C3.75441 6.90732 3.62724 6.96 3.49463 6.96Z" fill="#D9214E"/>
                </svg>
                {{ formatPercentage(b2bPercentageChange) }}
              </span>
            </div>
            <p class="small card-date mb-3">{{ currentMonthRange }}</p>
            
            <div class="border-top pt-3 mt-3">
              <div class="d-flex justify-content-between align-items-center">
                <span class="medium card-label">Load In</span>
                <div class="d-flex align-items-center">
                  <span class="fw-bold card-value-dark">
                    ₱{{ formatBusinessSegmentValue(eloadSummary.b2bLoadInMTD) }}
                  </span>
                  <span :class="['small fw-medium cursor-pointer d-inline-flex align-items-center rounded percentage-badge-small', getPercentageClass(b2bLoadInPercentageChange)]" data-bs-toggle="tooltip" data-bs-placement="right" :title="formatTooltip(eloadSummary.b2bLoadInPMMTD)">
                    <svg v-if="b2bLoadInPercentageChange > 0.0" xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                      <path d="M6.46477 3.9629C6.39897 3.96328 6.33374 3.95067 6.27282 3.92579C6.2119 3.9009 6.15649 3.86424 6.10977 3.8179L3.49477 1.2079L0.879772 3.8229C0.834948 3.87524 0.779789 3.91775 0.717757 3.94776C0.655725 3.97777 0.588158 3.99464 0.519298 3.9973C0.450439 3.99996 0.381773 3.98836 0.31761 3.96322C0.253448 3.93808 0.195174 3.89995 0.146447 3.85122C0.0977193 3.8025 0.0595904 3.74422 0.0344531 3.68006C0.00931581 3.6159 -0.00228716 3.54723 0.00037259 3.47837C0.00303234 3.40951 0.0198974 3.34195 0.0499091 3.27991C0.0799207 3.21788 0.122431 3.16272 0.174772 3.1179L3.13977 0.147899C3.18625 0.101035 3.24155 0.0638379 3.30248 0.0384536C3.36341 0.0130692 3.42877 0 3.49477 0C3.56078 0 3.62613 0.0130692 3.68706 0.0384536C3.74799 0.0638379 3.80329 0.101035 3.84977 0.147899L6.81477 3.1179C6.9079 3.21158 6.96017 3.33831 6.96017 3.4704C6.96017 3.60249 6.9079 3.72922 6.81477 3.8229C6.72084 3.91344 6.59524 3.96368 6.46477 3.9629Z" fill="#50CD89"/>
                      <path d="M3.49463 6.96293C3.36202 6.96293 3.23484 6.91025 3.14108 6.81648C3.04731 6.72272 2.99463 6.59554 2.99463 6.46293V0.50293C2.99463 0.370321 3.04731 0.243145 3.14108 0.149376C3.23484 0.0556082 3.36202 0.00292969 3.49463 0.00292969C3.62724 0.00292969 3.75441 0.0556082 3.84818 0.149376C3.94195 0.243145 3.99463 0.370321 3.99463 0.50293V6.46293C3.99463 6.59554 3.94195 6.72272 3.84818 6.81648C3.75441 6.91025 3.62724 6.96293 3.49463 6.96293Z" fill="#50CD89"/>
                    </svg>
                    <svg v-else-if="b2bLoadInPercentageChange < 0.0" xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                      <path d="M3.49477 6.95996C3.42897 6.96034 3.36374 6.94773 3.30282 6.92284C3.2419 6.89796 3.18649 6.8613 3.13977 6.81496L0.174772 3.81996C0.122431 3.77513 0.0799207 3.71997 0.0499091 3.65794C0.0198974 3.59591 0.00303234 3.52834 0.00037259 3.45948C-0.00228716 3.39062 0.00931581 3.32196 0.0344531 3.2578C0.0595904 3.19363 0.0977193 3.13536 0.146447 3.08663C0.195174 3.0379 0.253448 2.99978 0.31761 2.97464C0.381773 2.9495 0.450439 2.9379 0.519299 2.94056C0.588158 2.94322 0.655725 2.96008 0.717757 2.99009C0.779789 3.02011 0.834948 3.06262 0.879772 3.11496L3.49477 5.72996L6.10977 3.11496C6.1546 3.06262 6.20975 3.02011 6.27179 2.99009C6.33382 2.96008 6.40139 2.94322 6.47025 2.94056C6.53911 2.9379 6.60777 2.9495 6.67193 2.97464C6.7361 2.99978 6.79437 3.0379 6.8431 3.08663C6.89182 3.13536 6.92995 3.19363 6.95509 3.2578C6.98023 3.32196 6.99183 3.39062 6.98917 3.45948C6.98651 3.52834 6.96965 3.59591 6.93964 3.65794C6.90962 3.71997 6.86711 3.77513 6.81477 3.81996L3.84977 6.78996C3.80518 6.84084 3.75072 6.88214 3.6897 6.91136C3.62868 6.94058 3.56237 6.95711 3.49477 6.95996Z" fill="#D9214E"/>
                      <path d="M3.49463 6.96C3.36202 6.96 3.23484 6.90732 3.14108 6.81355C3.04731 6.71979 2.99463 6.59261 2.99463 6.46V0.5C2.99463 0.367392 3.04731 0.240215 3.14108 0.146447C3.23484 0.0526785 3.36202 0 3.49463 0C3.62724 0 3.75441 0.0526785 3.84818 0.146447C3.94195 0.240215 3.99463 0.367392 3.99463 0.5V6.46C3.99463 6.59261 3.94195 6.71979 3.84818 6.81355C3.75441 6.90732 3.62724 6.96 3.49463 6.96Z" fill="#D9214E"/>
                    </svg>
                    {{ formatPercentage(b2bLoadInPercentageChange) }}
                  </span>
                </div>
              </div>
              <div class="d-flex justify-content-between align-items-center">
                <span class="medium card-label">Load Out</span>
                <div class="d-flex align-items-center">
                  <span class="fw-bold card-value-dark">
                    ₱{{ formatBusinessSegmentValue(eloadSummary.b2bLoadOutMTD) }}
                  </span>
                  <span :class="['small fw-medium cursor-pointer d-inline-flex align-items-center rounded percentage-badge-small', getPercentageClass(b2bLoadOutPercentageChange)]" data-bs-toggle="tooltip" data-bs-placement="right" :title="formatTooltip(eloadSummary.b2bLoadOutPMMTD)">
                    <svg v-if="b2bLoadOutPercentageChange > 0.0" xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                      <path d="M6.46477 3.9629C6.39897 3.96328 6.33374 3.95067 6.27282 3.92579C6.2119 3.9009 6.15649 3.86424 6.10977 3.8179L3.49477 1.2079L0.879772 3.8229C0.834948 3.87524 0.779789 3.91775 0.717757 3.94776C0.655725 3.97777 0.588158 3.99464 0.519298 3.9973C0.450439 3.99996 0.381773 3.98836 0.31761 3.96322C0.253448 3.93808 0.195174 3.89995 0.146447 3.85122C0.0977193 3.8025 0.0595904 3.74422 0.0344531 3.68006C0.00931581 3.6159 -0.00228716 3.54723 0.00037259 3.47837C0.00303234 3.40951 0.0198974 3.34195 0.0499091 3.27991C0.0799207 3.21788 0.122431 3.16272 0.174772 3.1179L3.13977 0.147899C3.18625 0.101035 3.24155 0.0638379 3.30248 0.0384536C3.36341 0.0130692 3.42877 0 3.49477 0C3.56078 0 3.62613 0.0130692 3.68706 0.0384536C3.74799 0.0638379 3.80329 0.101035 3.84977 0.147899L6.81477 3.1179C6.9079 3.21158 6.96017 3.33831 6.96017 3.4704C6.96017 3.60249 6.9079 3.72922 6.81477 3.8229C6.72084 3.91344 6.59524 3.96368 6.46477 3.9629Z" fill="#50CD89"/>
                      <path d="M3.49463 6.96293C3.36202 6.96293 3.23484 6.91025 3.14108 6.81648C3.04731 6.72272 2.99463 6.59554 2.99463 6.46293V0.50293C2.99463 0.370321 3.04731 0.243145 3.14108 0.149376C3.23484 0.0556082 3.36202 0.00292969 3.49463 0.00292969C3.62724 0.00292969 3.75441 0.0556082 3.84818 0.149376C3.94195 0.243145 3.99463 0.370321 3.99463 0.50293V6.46293C3.99463 6.59554 3.94195 6.72272 3.84818 6.81648C3.75441 6.91025 3.62724 6.96293 3.49463 6.96293Z" fill="#50CD89"/>
                    </svg>
                    <svg v-else-if="b2bLoadOutPercentageChange < 0.0" xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                      <path d="M3.49477 6.95996C3.42897 6.96034 3.36374 6.94773 3.30282 6.92284C3.2419 6.89796 3.18649 6.8613 3.13977 6.81496L0.174772 3.81996C0.122431 3.77513 0.0799207 3.71997 0.0499091 3.65794C0.0198974 3.59591 0.00303234 3.52834 0.00037259 3.45948C-0.00228716 3.39062 0.00931581 3.32196 0.0344531 3.2578C0.0595904 3.19363 0.0977193 3.13536 0.146447 3.08663C0.195174 3.0379 0.253448 2.99978 0.31761 2.97464C0.381773 2.9495 0.450439 2.9379 0.519299 2.94056C0.588158 2.94322 0.655725 2.96008 0.717757 2.99009C0.779789 3.02011 0.834948 3.06262 0.879772 3.11496L3.49477 5.72996L6.10977 3.11496C6.1546 3.06262 6.20975 3.02011 6.27179 2.99009C6.33382 2.96008 6.40139 2.94322 6.47025 2.94056C6.53911 2.9379 6.60777 2.9495 6.67193 2.97464C6.7361 2.99978 6.79437 3.0379 6.8431 3.08663C6.89182 3.13536 6.92995 3.19363 6.95509 3.2578C6.98023 3.32196 6.99183 3.39062 6.98917 3.45948C6.98651 3.52834 6.96965 3.59591 6.93964 3.65794C6.90962 3.71997 6.86711 3.77513 6.81477 3.81996L3.84977 6.78996C3.80518 6.84084 3.75072 6.88214 3.6897 6.91136C3.62868 6.94058 3.56237 6.95711 3.49477 6.95996Z" fill="#D9214E"/>
                      <path d="M3.49463 6.96C3.36202 6.96 3.23484 6.90732 3.14108 6.81355C3.04731 6.71979 2.99463 6.59261 2.99463 6.46V0.5C2.99463 0.367392 3.04731 0.240215 3.14108 0.146447C3.23484 0.0526785 3.36202 0 3.49463 0C3.62724 0 3.75441 0.0526785 3.84818 0.146447C3.94195 0.240215 3.99463 0.367392 3.99463 0.5V6.46C3.99463 6.59261 3.94195 6.71979 3.84818 6.81355C3.75441 6.90732 3.62724 6.96 3.49463 6.96Z" fill="#D9214E"/>
                    </svg>
                    {{ formatPercentage(b2bLoadOutPercentageChange) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary P&L Tabs Section -->
    <div class="row mt-4">
      <div class="col-12">
        <div class="card border-0 shadow-sm">
          <div class="card-body">
            <!-- Tab Navigation -->
            <div class="d-flex border-bottom mb-3">
              <button 
                @click="setActiveTab('business-segment')"
                :class="['btn btn-link text-decoration-none px-3 py-2 border-0 rounded-0', { 'border-bottom border-primary tab-view-active-label': activeTab === 'business-segment', 'tab-view-inactive-label': activeTab !== 'business-segment' }]"
              >
                Business Segment
              </button>
              <button 
                @click="setActiveTab('summary-pnl')"
                :class="['btn btn-link text-decoration-none px-3 py-2 border-0 rounded-0', { 'border-bottom border-primary tab-view-active-label': activeTab === 'summary-pnl', 'tab-view-inactive-label': activeTab !== 'summary-pnl' }]"
              >
                Summary P&L
              </button>
            </div>

            <!-- Tab Content -->
            <!-- Business Segment Tab Content -->
            <div v-show="activeTab === 'business-segment'" class="tab-content">
              <div class="row">
                <div class="col-12">
                  <!-- Unified Table Structure -->
                  <div class="d-flex">
                    <!-- Fixed Left Column -->
                    <div style="min-width: 200px; position: relative; z-index: 1; box-shadow: 4px 0 8px rgba(0, 0, 0, 0.08); display: flex; flex-direction: column; align-self: stretch; border-right: 2px solid #dee2e6; margin-top: -2px;">
                      <div style="padding: 1rem 1rem; font-size: 0.95rem; background-color: #ffffff; border-bottom: 2px solid #dee2e6; line-height: 1.5; color: var(--Gray-Gray-800, #3F4254); display: flex; align-items: center; box-sizing: border-box;" class="fw-semibold">Channels</div>
                      <div style="padding: 1rem 1rem; border-bottom: 2px solid #dee2e6; font-size: 1.125rem; background-color: #F8F5FF; line-height: 1.5; font-weight: 600; color: var(--Gray-Gray-800, #3F4254); display: flex; align-items: center; box-sizing: border-box;" class="">Period View</div>
                      <div style="padding: 1rem 1rem; border-bottom: 2px solid #dee2e6; font-size: 1.125rem; background-color: #F8F5FF; font-weight: 600; line-height: 1.5; color: var(--Gray-Gray-800, #3F4254); display: flex; align-items: center; box-sizing: border-box;" class="">Revenue</div>
                      <div style="padding: 1rem 1rem; border-bottom: 2px solid #dee2e6; font-size: 1.125rem; background-color: #F8F5FF; font-weight: 600; line-height: 1.5; color: var(--Gray-Gray-800, #3F4254); display: flex; align-items: center; box-sizing: border-box;" class="">CM1</div>
                      <div style="padding: 1rem 1rem; border-bottom: 2px solid #dee2e6; font-size: 1.125rem; background-color: #F8F5FF; font-weight: 600; line-height: 1.5; color: var(--Gray-Gray-800, #3F4254); display: flex; align-items: center; box-sizing: border-box;" class="">CM2</div>
                      <div style="padding: 1rem 1rem; font-size: 1.125rem; background-color: #F8F5FF; font-weight: 600; line-height: 1.5; color: var(--Gray-Gray-800, #3F4254); display: flex; align-items: center; box-sizing: border-box;" class=""># of Active Users</div>
                    </div>
                    
                    <!-- Scrollable Data Area -->
                    <div class="flex-grow-1 me-1" style="overflow-x: auto; padding-left: 0; margin-left: 8px;">
                      <table class="table table-borderless mb-0 business-segment-table" style="min-width: 900px;">
                        <thead>
                          <tr class="border-bottom">
                            <th colspan="3" class="text-start text-muted fw-semibold" style="padding: 1rem; font-size: 0.95rem; background-color: #ffffff;">Retail</th>
                            <th colspan="3" class="text-start text-muted fw-semibold" style="padding: 1rem; font-size: 0.95rem; background-color: #ffffff;">B2B</th>
                            <th colspan="3" class="text-start text-muted fw-semibold" style="padding: 1rem; font-size: 0.95rem; background-color: #ffffff;">Total</th>
                          </tr>
                          <tr class="border-bottom">
                            <th class="text-start" style="background: #f9f9f9; color: #7E8299; width: 100px; padding: 1rem 1rem; font-size: 1rem; font-weight: 600;">PM</th>
                            <th class="text-start" style="background: #f9f9f9; color: #7E8299; width: 100px; padding: 1rem 1rem; font-size: 1rem; font-weight: 600;">PM MTD</th>
                            <th class="text-start" style="background: #f9f9f9; color: #7E8299; width: 100px; padding: 1rem 1rem; font-size: 1rem; font-weight: 600;">MTD</th>
                            <th class="text-start" style="color: #7E8299; width: 100px; padding: 1rem 1rem; font-size: 1rem; font-weight: 600;">PM</th>
                            <th class="text-start" style="color: #7E8299; width: 100px; padding: 1rem 1rem; font-size: 1rem; font-weight: 600;">PM MTD</th>
                            <th class="text-start" style="color: #7E8299; width: 100px; padding: 1rem 1rem; font-size: 1rem; font-weight: 600;">MTD</th>
                            <th class="text-start" style="background: #f9f9f9; color: #7E8299; width: 100px; padding: 1rem 1rem; font-size: 1rem; font-weight: 600;">PM</th>
                            <th class="text-start" style="background: #f9f9f9; color: #7E8299; width: 100px; padding: 1rem 1rem; font-size: 1rem; font-weight: 600;">PM MTD</th>
                            <th class="text-start" style="background: #f9f9f9; color: #7E8299; width: 100px; padding: 1rem 1rem; font-size: 1rem; font-weight: 600;">MTD</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.retailLoadInPM)">{{ formatBusinessSegmentValue(eloadSummary.retailLoadInPM) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.retailLoadInPMMTD)">{{ formatBusinessSegmentValue(eloadSummary.retailLoadInPMMTD) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.retailLoadInMTD)">{{ formatBusinessSegmentValue(eloadSummary.retailLoadInMTD) }}</span></td>
                            <td class="text-end"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.b2bLoadInPM)">{{ formatBusinessSegmentValue(eloadSummary.b2bLoadInPM) }}</span></td>
                            <td class="text-end"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.b2bLoadInPMMTD)">{{ formatBusinessSegmentValue(eloadSummary.b2bLoadInPMMTD) }}</span></td>
                            <td class="text-end"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.b2bLoadInMTD)">{{ formatBusinessSegmentValue(eloadSummary.b2bLoadInMTD) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(totalLoadInPM)">{{ formatBusinessSegmentValue(totalLoadInPM) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(totalLoadInPMMTD)">{{ formatBusinessSegmentValue(totalLoadInPMMTD) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(totalLoadInMTD)">{{ formatBusinessSegmentValue(totalLoadInMTD) }}</span></td>
                          </tr>
                          <tr>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.retailCM1PM)">{{ formatBusinessSegmentValue(eloadSummary.retailCM1PM) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.retailCM1PMMTD)">{{ formatBusinessSegmentValue(eloadSummary.retailCM1PMMTD) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.retailCM1MTD)">{{ formatBusinessSegmentValue(eloadSummary.retailCM1MTD) }}</span></td>
                            <td class="text-end"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.b2bCM1PM)">{{ formatBusinessSegmentValue(eloadSummary.b2bCM1PM) }}</span></td>
                            <td class="text-end"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.b2bCM1PMMTD)">{{ formatBusinessSegmentValue(eloadSummary.b2bCM1PMMTD) }}</span></td>
                            <td class="text-end"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.b2bCM1MTD)">{{ formatBusinessSegmentValue(eloadSummary.b2bCM1MTD) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(totalCM1PM)">{{ formatBusinessSegmentValue(totalCM1PM) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(totalCM1PMMTD)">{{ formatBusinessSegmentValue(totalCM1PMMTD) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(totalCM1MTD)">{{ formatBusinessSegmentValue(totalCM1MTD) }}</span></td>
                          </tr>
                          <tr>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.retailCM2PM)">{{ formatBusinessSegmentValue(eloadSummary.retailCM2PM) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.retailCM2PMMTD)">{{ formatBusinessSegmentValue(eloadSummary.retailCM2PMMTD) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.retailCM2MTD)">{{ formatBusinessSegmentValue(eloadSummary.retailCM2MTD) }}</span></td>
                            <td class="text-end"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.b2bCM2PM)">{{ formatBusinessSegmentValue(eloadSummary.b2bCM2PM) }}</span></td>
                            <td class="text-end"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.b2bCM2PMMTD)">{{ formatBusinessSegmentValue(eloadSummary.b2bCM2PMMTD) }}</span></td>
                            <td class="text-end"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.b2bCM2MTD)">{{ formatBusinessSegmentValue(eloadSummary.b2bCM2MTD) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(totalCM2PM)">{{ formatBusinessSegmentValue(totalCM2PM) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(totalCM2PMMTD)">{{ formatBusinessSegmentValue(totalCM2PMMTD) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(totalCM2MTD)">{{ formatBusinessSegmentValue(totalCM2MTD) }}</span></td>
                          </tr>
                          <tr>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="formatCountTooltip(activeUsers.retailActiveUsersPM)">{{ formatValue(activeUsers.retailActiveUsersPM) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="formatCountTooltip(activeUsers.retailActiveUsersPMMTD)">{{ formatValue(activeUsers.retailActiveUsersPMMTD) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="formatCountTooltip(activeUsers.retailActiveUsersMTD)">{{ formatValue(activeUsers.retailActiveUsersMTD) }}</span></td>
                            <td class="text-end"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="formatCountTooltip(activeUsers.b2bActiveUsersPM)">{{ formatValue(activeUsers.b2bActiveUsersPM) }}</span></td>
                            <td class="text-end"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="formatCountTooltip(activeUsers.b2bActiveUsersPMMTD)">{{ formatValue(activeUsers.b2bActiveUsersPMMTD) }}</span></td>
                            <td class="text-end"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="formatCountTooltip(activeUsers.b2bActiveUsersMTD)">{{ formatValue(activeUsers.b2bActiveUsersMTD) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="formatCountTooltip(totalActiveUsersPM)">{{ formatValue(totalActiveUsersPM) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="formatCountTooltip(totalActiveUsersPMMTD)">{{ formatValue(totalActiveUsersPMMTD) }}</span></td>
                            <td class="text-end" style="background: #f9f9f9;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="formatCountTooltip(totalActiveUsersMTD)">{{ formatValue(totalActiveUsersMTD) }}</span></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Summary P&L Tab Content -->
            <div v-show="activeTab === 'summary-pnl'" class="tab-content">
              <div class="row">
                <div class="col-12">
                  <div class="table-responsive">
                    <table class="table table-borderless mb-0" style="font-size: 0.9375rem;">
                      <thead>
                        <tr class="border-bottom" style="border-bottom-width: 2px !important;">
                          <th class="fw-normal" style="padding: 0.75rem 0; font-size: 0.875rem; color: #7E8299; font-weight: 600;">Period View</th>
                          <th class="fw-normal" style="padding: 0.75rem 0; font-size: 0.875rem;"></th>
                          <th class="fw-normal text-end" style="padding: 0.75rem 0; font-size: 0.875rem; color: #7E8299; font-weight: 600;">Prior Month<br><small style="font-size: 0.75rem; color: #3F4254; font-weight: 600;">{{ priorMonthLabel }}</small></th>
                          <th class="fw-normal text-end" style="padding: 0.75rem 0; font-size: 0.875rem; color: #7E8299; font-weight: 600;">PM MTD<br><small style="font-size: 0.75rem; color: #3F4254; font-weight: 600;">{{ pmMtdLabel }}</small></th>
                          <th class="fw-normal text-end" style="padding: 0.75rem 0; font-size: 0.875rem; color: #7E8299; font-weight: 600;">MTD<br><small style="font-size: 0.75rem; color: #3F4254; font-weight: 600;">{{ mtdLabel }}</small></th>
                        </tr>
                      </thead>
                      <tbody>
                        <!-- Total E-Load Section -->
                        <tr>
                          <td rowspan="3" class="fw-bold align-top" style="padding: 0.5rem 0; color: #3F4254;">Total E-Load</td>
                          <td style="padding: 0.5rem 0; color: #A1A5B7;">Load In</td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(totalLoadInPM)">{{ formatBusinessSegmentValue(totalLoadInPM) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(totalLoadInPMMTD)">{{ formatBusinessSegmentValue(totalLoadInPMMTD) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(totalLoadInMTD)">{{ formatBusinessSegmentValue(totalLoadInMTD) }}</span></td>
                        </tr>
                        <tr>
                          <td style="padding: 0.5rem 0; color: #A1A5B7;">Load Out</td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(totalLoadOutPM)">{{ formatBusinessSegmentValue(totalLoadOutPM) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(totalLoadOutPMMTD)">{{ formatBusinessSegmentValue(totalLoadOutPMMTD) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(totalLoadOutMTD)">{{ formatBusinessSegmentValue(totalLoadOutMTD) }}</span></td>
                        </tr>
                        <tr class="border-bottom" style="border-bottom-width: 2px !important">
                          <td style="padding: 0.5rem 0 1rem 0; color: #A1A5B7; font-weight: 600;">Total</td>
                          <td class="text-end" style="padding: 0.5rem 0 1rem 0; font-weight: 600; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(totalEloadPM)">{{ formatBusinessSegmentValue(totalEloadPM) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0 1rem 0; font-weight: 600; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(totalEloadPMMTD)">{{ formatBusinessSegmentValue(totalEloadPMMTD) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0 1rem 0; font-weight: 600; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(totalEloadMTD)">{{ formatBusinessSegmentValue(totalEloadMTD) }}</span></td>
                        </tr>
                        <!-- Retail Section -->
                        <tr>
                          <td rowspan="3" class="fw-bold align-top" style="padding: 0.5rem 0; color: #3F4254;">Retail</td>
                          <td style="padding: 0.5rem 0; color: #A1A5B7;">Load In</td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.retailLoadInPM)">{{ formatBusinessSegmentValue(eloadSummary.retailLoadInPM) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.retailLoadInPMMTD)">{{ formatBusinessSegmentValue(eloadSummary.retailLoadInPMMTD) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.retailLoadInMTD)">{{ formatBusinessSegmentValue(eloadSummary.retailLoadInMTD) }}</span></td>
                        </tr>
                        <tr>
                          <td style="padding: 0.5rem 0; color: #A1A5B7;">Load Out</td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.retailLoadOutPM)">{{ formatBusinessSegmentValue(eloadSummary.retailLoadOutPM) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.retailLoadOutPMMTD)">{{ formatBusinessSegmentValue(eloadSummary.retailLoadOutPMMTD) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.retailLoadOutMTD)">{{ formatBusinessSegmentValue(eloadSummary.retailLoadOutMTD) }}</span></td>
                        </tr>
                        <tr>
                          <td style="padding: 0.5rem 0; color: #A1A5B7; font-weight: 600;">Total</td>
                          <td class="text-end" style="padding: 0.5rem 0; font-weight: 600; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(retailTotalPM)">{{ formatBusinessSegmentValue(retailTotalPM) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0; font-weight: 600; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(retailTotalPMMTD)">{{ formatBusinessSegmentValue(retailTotalPMMTD) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0; font-weight: 600; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(retailTotalMTD)">{{ formatBusinessSegmentValue(retailTotalMTD) }}</span></td>
                        </tr>
                        <tr class="border-bottom border-top" style="border-top-width: 2px !important; border-bottom-width: 2px !important;">
                          <td colspan="2" class="fw-bold" style="padding: 0.5rem 0 1rem 0; color: #3F4254;"># of Active Users</td>
                          <td class="text-end" style="padding: 0.5rem 0 1rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="formatCountTooltip(activeUsers.retailActiveUsersPM)">{{ formatValue(activeUsers.retailActiveUsersPM) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0 1rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="formatCountTooltip(activeUsers.retailActiveUsersPMMTD)">{{ formatValue(activeUsers.retailActiveUsersPMMTD) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0 1rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="formatCountTooltip(activeUsers.retailActiveUsersMTD)">{{ formatValue(activeUsers.retailActiveUsersMTD) }}</span></td>
                        </tr>
                        <!-- B2B Section -->
                        <tr>
                          <td rowspan="3" class="fw-bold align-top" style="padding: 0.5rem 0; color: #3F4254;">B2B</td>
                          <td style="padding: 0.5rem 0; color: #A1A5B7;">Load In</td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.b2bLoadInPM)">{{ formatBusinessSegmentValue(eloadSummary.b2bLoadInPM) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.b2bLoadInPMMTD)">{{ formatBusinessSegmentValue(eloadSummary.b2bLoadInPMMTD) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.b2bLoadInMTD)">{{ formatBusinessSegmentValue(eloadSummary.b2bLoadInMTD) }}</span></td>
                        </tr>
                        <tr>
                          <td style="padding: 0.5rem 0; color: #A1A5B7;">Load Out</td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.b2bLoadOutPM)">{{ formatBusinessSegmentValue(eloadSummary.b2bLoadOutPM) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.b2bLoadOutPMMTD)">{{ formatBusinessSegmentValue(eloadSummary.b2bLoadOutPMMTD) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(eloadSummary.b2bLoadOutMTD)">{{ formatBusinessSegmentValue(eloadSummary.b2bLoadOutMTD) }}</span></td>
                        </tr>
                        <tr>
                          <td style="padding: 0.5rem 0; color: #A1A5B7; font-weight: 600;">Total</td>
                          <td class="text-end" style="padding: 0.5rem 0; font-weight: 600; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(b2bTotalPM)">{{ formatBusinessSegmentValue(b2bTotalPM) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0; font-weight: 600; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(b2bTotalPMMTD)">{{ formatBusinessSegmentValue(b2bTotalPMMTD) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0; font-weight: 600; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="getCompleteValue(b2bTotalMTD)">{{ formatBusinessSegmentValue(b2bTotalMTD) }}</span></td>
                        </tr>
                        <tr class="border-top" style="border-top-width: 2px !important;">
                          <td colspan="2" class="fw-bold" style="padding: 0.5rem 0; color: #3F4254;"># of Active Users</td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="formatCountTooltip(activeUsers.b2bActiveUsersPM)">{{ formatValue(activeUsers.b2bActiveUsersPM) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="formatCountTooltip(activeUsers.b2bActiveUsersPMMTD)">{{ formatValue(activeUsers.b2bActiveUsersPMMTD) }}</span></td>
                          <td class="text-end" style="padding: 0.5rem 0; color: #3F4254;"><span data-bs-toggle="tooltip" data-bs-placement="top" :title="formatCountTooltip(activeUsers.b2bActiveUsersMTD)">{{ formatValue(activeUsers.b2bActiveUsersMTD) }}</span></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Daily Transactions Section -->
    <div class="row mt-1">
      <div class="col-12">
        <div class="card border-0 shadow-sm">
          <div class="card-body">
            <!-- Tab Navigation for Daily Transactions -->
            <div class="d-flex border-bottom mb-3">
              <button 
                @click="setDailyTransactionTab('peddlr-lite')"
                :class="['btn btn-link text-decoration-none px-3 py-2 border-0 rounded-0 fw-semi', { 'border-bottom border-primary tab-view-active-label': dailyTransactionTab === 'peddlr-lite', 'tab-view-inactive-label': dailyTransactionTab !== 'peddlr-lite' }]"
              >
                Peddlr Lite
              </button>
              <button 
                @click="setDailyTransactionTab('b2b')"
                :class="['btn btn-link text-decoration-none px-3 py-2 border-0 rounded-0 fw-semi', { 'border-bottom border-primary tab-view-active-label': dailyTransactionTab === 'b2b', 'tab-view-inactive-label': dailyTransactionTab !== 'b2b' }]"
              >
                B2B
              </button>
              <button 
                @click="setDailyTransactionTab('provider-load-out')"
                :class="['btn btn-link text-decoration-none px-3 py-2 border-0 rounded-0 fw-semi', { 'border-bottom border-primary tab-view-active-label': dailyTransactionTab === 'provider-load-out', 'tab-view-inactive-label': dailyTransactionTab !== 'provider-load-out' }]"
              >
                Provider Load Out
              </button>
            </div>
            
            <!-- Sub-header with Date Filter -->
            <div class="d-flex justify-content-between align-items-start mb-3 mt-4">
              <h5 class="mb-0 daily-transaction-title">Daily Transaction:</h5>
              <div class="d-flex flex-column align-items-end gap-1">
                <div class="position-relative">
                  <flat-pickr
                    v-model="dateRangeString"
                    :config="flatpickrConfig"
                    class="form-control date-picker-input"
                    placeholder="Select date range"
                  />
                  <svg class="date-picker-chevron" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6L8 10L12 6" stroke="#181C32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <span class="filter-label">Filter by Date</span>
              </div>
            </div>
            
            <!-- Peddlr Lite Tab Content -->
            <div v-show="dailyTransactionTab === 'peddlr-lite'" class="table-responsive">
              <table class="table table-borderless daily-transaction-table">
                <thead>
                  <tr class="table-header-row">
                    <th class="table-date-label">Date:</th>
                    <th v-for="date in retailDailyDates" :key="date" class="table-date-header">
                      {{ formatDateHeader(date) }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Load In Summary Row -->
                  <tr class="table-data-row border-bottom">
                    <td class="table-row-label">Load In:</td>
                    <td v-for="date in retailDailyDates" :key="`load-in-${date}`" class="table-data-cell border-bottom" >
                      <span data-bs-toggle="tooltip" data-bs-placement="top" :title="getRetailTransactionCompleteValue(date, 'loadIn')">{{ getTransactionValue(date, 'loadIn') }}</span>
                    </td>
                  </tr>
                  <!-- Load Out Summary Row -->
                  <tr class="table-data-row">
                    <td class="table-row-label border-bottom">Load Out:</td>
                    <td v-for="date in retailDailyDates" :key="`load-out-${date}`" class="table-data-cell border-bottom">
                      <span data-bs-toggle="tooltip" data-bs-placement="top" :title="getRetailTransactionCompleteValue(date, 'loadOut')">{{ getTransactionValue(date, 'loadOut') }}</span>
                    </td>
                  </tr>
                  <!-- Unique Users Row -->  
                  <tr class="table-data-row">
                    <td class="table-row-label border-bottom">Unique Users:</td>
                    <td v-for="date in retailDailyDates" :key="`unique-users-${date}`" class="table-data-cell border-bottom">
                      <span data-bs-toggle="tooltip" data-bs-placement="top" :title="getRetailTransactionCompleteValue(date, 'uniqueUsers')">{{ getTransactionValue(date, 'uniqueUsers') }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- B2B Tab Content -->
            <div v-show="dailyTransactionTab === 'b2b'" class="table-responsive">
              <table class="table table-borderless daily-transaction-table">
                <thead>
                  <tr class="table-header-row">
                    <th class="table-date-label">Date:</th>
                    <th v-for="date in b2bDailyDates" :key="`b2b-${date}`" class="table-date-header">
                      {{ formatDateHeader(date) }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Load In Summary Row -->
                  <tr class="table-data-row">
                    <td class="table-row-label border-bottom">Load In:</td>
                    <td v-for="date in b2bDailyDates" :key="`b2b-load-in-${date}`" class="table-data-cell border-bottom">
                      <span data-bs-toggle="tooltip" data-bs-placement="top" :title="getB2BTransactionCompleteValue(date, 'loadIn')">{{ getTransactionValue(date, 'loadIn') }}</span>
                    </td>
                  </tr>
                  <!-- Load Out Summary Row -->
                  <tr class="table-data-row table-separator-row">
                    <td class="table-row-label border-bottom">Load Out:</td>
                    <td v-for="date in b2bDailyDates" :key="`b2b-load-out-${date}`" class="table-data-cell border-bottom">
                      <span data-bs-toggle="tooltip" data-bs-placement="top" :title="getB2BTransactionCompleteValue(date, 'loadOut')">{{ getTransactionValue(date, 'loadOut') }}</span>
                    </td>
                  </tr>
                  <!-- Provider Breakdown Header -->
                  <tr class="table-subheader-row">
                    <td class="table-subheader-label border-bottom">Load Out By Provider:</td>
                    <td v-for="date in b2bDailyDates" :key="`b2b-header-${date}`" class="table-data-cell border-bottom">
                    </td>
                  </tr>
                  <!-- Provider Rows -->
                  <tr v-for="provider in b2bDailyTransactionsPerProvider" :key="`b2b-${provider.name}`" class="table-data-row">
                    <td class="table-provider-label border-bottom">{{ provider.name }}</td>
                    <td v-for="date in b2bDailyDates" :key="`b2b-${provider.name}-${date}`" class="table-data-cell border-bottom">
                      <span data-bs-toggle="tooltip" data-bs-placement="top" :title="getB2BProviderCompleteValue(provider.name, date)">{{ getB2BProviderValue(provider.name, date) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Provider Load Out Tab Content -->
            <div v-show="dailyTransactionTab === 'provider-load-out'" class="table-responsive">
              <table class="table table-borderless daily-transaction-table">
                <thead>
                  <tr class="table-header-row">
                    <th class="table-date-label border-bottom">Provider Load Out</th>
                    <th v-for="date in dailyDates" :key="`provider-${date}`" class="table-date-header border-bottom">
                      {{ formatDateHeader(date) }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Provider Rows -->
                  <tr v-for="provider in dailyTransactionsPerProvider" :key="`provider-${provider.name}`" class="table-data-row">
                    <td class="table-row-label border-bottom">{{ provider.name }}</td>
                    <td v-for="date in dailyDates" :key="`provider-${provider.name}-${date}`" class="table-data-cell border-bottom">
                      <span data-bs-toggle="tooltip" data-bs-placement="top" :title="getProviderCompleteValue(provider.name, date)">{{ getProviderValue(provider.name, date) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* E-Load Dashboard Card Styles */
.card-label {
  color: #7E8299;
  font-weight: 600;
  font-size: 1rem;
}

.card-date {
  color: #A1A5B7;
}

.card-value-main {
  color: #3F4254;
}

.card-value-dark {
  color: #3F4254;
}

.peso-sign-gray {
  color: #A1A5B7;
  vertical-align: super;
  font-size: 0.6em;
}

/* Percentage badge text colors */
.text-neutral {
  color: #AFAFAF !important;
}

.cursor-pointer {
  cursor: pointer;
}
.percentage-badge-main {
  min-width: 70px;
  justify-content: center;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem !important;
}

.percentage-badge-small {
  min-width: unset !important;
  width: auto !important;
  justify-content: flex-start !important;
  margin-left: 0.2rem !important;
  font-size: 0.65rem !important;
  padding: 0.15rem 0.35rem !important;
  gap: 0.15rem !important;
}

/* Table cell tooltips */
td[data-bs-toggle="tooltip"] {
  cursor: pointer;
  position: relative;
}

/* Business Segment Table Styling */
.business-segment-table tbody td {
  padding: 1rem 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: #3F4254;
  vertical-align: middle;
}

.business-segment-table tbody tr {
  border-bottom: 1px solid #dee2e6;
}

.business-segment-table tbody tr:last-child {
  border-bottom: none;
}

.business-segment-table thead tr.border-bottom,
.business-segment-table tr.border-bottom {
  border-bottom: 2px solid #dee2e6 !important;
}
.business-segment-table tbody td {
  border-bottom: 2px solid #dee2e6;
}

.business-segment-table tbody tr:last-child td {
  border-bottom: none !important;
}

.tab-view-active-label {
  color: var(--Gray-Gray-800, #3F4254);
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  border-bottom-width: 2px !important;
}
.tab-view-inactive-label {
  color: var(--Gray-Gray-500, #7E8299);
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  cursor: pointer; 
}

/* Daily Transaction Table Styles */
.daily-transaction-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #181C32;
  line-height: 1.5;
}

.filter-label {
  font-size: 0.875rem;
  font-weight: 400;
  color: #7E8299;
  white-space: nowrap;
}

.date-picker-input {
  min-width: 300px !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  border: 1px solid #E1E3EA !important;
  border-radius: 0.475rem !important;
  padding: 0.5rem 3rem 0.5rem 0.75rem !important;
  color: #181C32 !important;
  cursor: pointer !important;
}

/* Use deep selector to style flatpickr's dynamically created altInput while keeping scoped */
::v-deep .flatpickr-input,
::v-deep .flatpickr-input.active,
::v-deep .flatpickr-input.input,
::v-deep input.flatpickr-input[readonly],
::v-deep .form-control.date-picker-input.input {
  font-weight: 600 !important;
  padding-right: 0.8rem !important;
  font-size: 14px !important;
  min-width: 230px !important;
  color: #181C32 !important;
}

::v-deep .flatpickr-input:focus {
  border-color: #009EF7 !important;
  box-shadow: 0 0 0 0.2rem rgba(0, 158, 247, 0.1) !important;
}

.date-picker-input:focus {
  border-color: #009EF7 !important;
  box-shadow: 0 0 0 0.2rem rgba(0, 158, 247, 0.1) !important;
}

.date-picker-chevron {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 10;
}
  
/* Daily Transaction Table */
.daily-transaction-table {
  margin-bottom: 0;
}

.daily-transaction-table .table-header-row {
  border-bottom: 1px solid #E1E3EA;
}

.daily-transaction-table .table-date-label {
  font-size: 0.875rem;
  font-weight: 700;;
  color: #000000;
  padding: 0.75rem 1rem 0.75rem 0;
  border: none;
  text-align: left;
  white-space: nowrap;
}

.daily-transaction-table .table-date-header {
  font-size: 0.875rem;
  color: #000000;
  padding: 0.75rem 1rem;
  border: none;
  font-weight: 700;
  text-align: right;
  white-space: nowrap;
}

.daily-transaction-table .table-data-row {
  border: none;
}

.daily-transaction-table .table-row-label {
  font-size: 1rem;
  font-weight: 600;
  color: #181C32;
  padding: 1rem 1rem 1rem 0;
  text-align: left;
  white-space: nowrap;
  border: none;
}

.daily-transaction-table .table-data-cell {
  font-size: 1rem;
  font-weight: 400;
  color: #181C32;
  padding: 1rem 1rem;
  text-align: right;
  white-space: nowrap;
  border: none;
}

.daily-transaction-table .table-separator-row {
  border-bottom: 1px solid #E1E3EA;
}

.daily-transaction-table .table-separator-row td {
  padding-bottom: 1.5rem;
}

.daily-transaction-table .table-subheader-row {
  border: none;
}

.daily-transaction-table .table-subheader-label {
  font-size: 1rem;
  font-weight: 600;
  color: #181C32;
  padding: 1.5rem 1rem 1rem 0;
  text-align: left;
  white-space: nowrap;
  border: none;
}

.daily-transaction-table .table-subheader-row td {
  padding-top: 1.5rem;
}

.daily-transaction-table .table-provider-label {
  font-size: 1rem;
  font-weight: 400;
  color: #181C32;
  padding: 1rem 1rem 1rem 2rem;
  text-align: left;
  white-space: nowrap;
  border: none;
}

/* Custom styles to complement Bootstrap */
.table th {
  border-top: none;
  font-size: 0.85rem;
  padding: 0.75rem 0.5rem;
}

.table td {
  padding: 0.5rem;
  font-size: 0.875rem;
  vertical-align: middle;
}

.table-borderless td,
.table-borderless th {
  border: none;
}

/* Cursor pointer for interactive elements */
.cursor-pointer {
  cursor: pointer;
}


/* Ensure proper spacing for responsive design */
@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.8rem;
  }
  
  .card-body {
    padding: 1rem 0.75rem;
  }
  
  /* Stack cards on mobile */
  .col-lg-4 {
    margin-bottom: 1rem;
  }
}

/* Small mobile devices */
@media (max-width: 576px) {
  .table-responsive {
    font-size: 0.75rem;
  }
  
  .card-body {
    padding: 0.75rem 0.5rem;
  }
  
  h3 {
    font-size: 1.5rem;
  }
}

.coming-soon-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.75);
  z-index: 5;
  display: flex;
  justify-content: center;
  align-items: center;
}

.coming-soon-content {
  background: white;
  padding: 3rem 2rem;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90%;
  animation: fadeInUp 0.5s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>

<style>
.dashboard-tooltip-left .tooltip-inner {
  text-align: left !important;
}
.dashboard-tooltip-left .tooltip-inner .dashboard-tooltip-title {
  font-weight: 600;
  font-size: 0.875rem;
}
</style>
